import React, { useContext } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  useEmailPreferences,
  useUpdateEmailPreferences,
} from "../../app/queries/notifications";
import { NetworkErrorAlertContext } from "../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../context/SuccessAlertContext";
import CustomModal from "../common/CustomModal";
import {
  ColumnLabel,
  ModalBody,
  ModalSubtitle,
  ModalTitle,
  PreferenceRow,
  PreferenceSwitch,
  RowDescription,
  RowLabel,
  RowText,
  Section,
  SectionHeader,
  SectionTitle,
  SwitchCell,
} from "./preferencesModalStyles";
import { EmailPreferenceFlags } from "./types";

const ACCOUNT_PREFERENCES: {
  label: string;
  key: keyof EmailPreferenceFlags;
}[] = [
  { label: "Virtual accounts", key: "virtualAccounts" },
  { label: "Wallet funding", key: "walletFunding" },
  { label: "Payout", key: "payout" },
  { label: "Klasha Wire", key: "klashaWire" },
  { label: "Pay to Asia", key: "payToAsia" },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

const NotificationPreferencesModal = ({ open, onClose }: Props) => {
  const { businessId } = useAppSelector((state) => state.users);
  const { onShowAlert } = useContext(NetworkErrorAlertContext);
  const { onShowAlert: onShowSuccess } = useContext(SuccessAlertContext) || {};
  const { data, isLoading } = useEmailPreferences(businessId, open);
  const updatePreferences = useUpdateEmailPreferences(businessId);

  const handleCustomerPaymentsChange = (checked: boolean) => {
    updatePreferences.mutate(
      { customerPayments: checked },
      {
        onSuccess: () => {
          onShowSuccess?.(
            `Customer payment emails turned ${checked ? "on" : "off"}`,
          );
        },
        onError: (error: any) => {
          onShowAlert?.(
            error,
            error?.response?.data?.message ||
              "Unable to update notification preferences.",
          );
        },
      },
    );
  };

  const handleAccountPreferenceChange = (
    key: keyof EmailPreferenceFlags,
    label: string,
    checked: boolean,
  ) => {
    updatePreferences.mutate(
      { [key]: checked },
      {
        onSuccess: () => {
          onShowSuccess?.(`${label} emails turned ${checked ? "on" : "off"}`);
        },
        onError: (error: any) => {
          onShowAlert?.(
            error,
            error?.response?.data?.message ||
              "Unable to update notification preferences.",
          );
        },
      },
    );
  };

  return (
    <CustomModal isModalVisible={open} onClose={onClose} width="480px">
      <ModalBody>
        <ModalTitle>Notification preferences</ModalTitle>
        <ModalSubtitle>Choose which updates reach your email.</ModalSubtitle>

        <Section>
          <SectionHeader>
            <SectionTitle>Account</SectionTitle>
            <ColumnLabel>Email</ColumnLabel>
          </SectionHeader>
          {ACCOUNT_PREFERENCES.map(({ label, key }) => (
            <PreferenceRow key={key}>
              <RowLabel>{label}</RowLabel>
              <SwitchCell>
                <PreferenceSwitch
                  checked={data?.[key] ?? false}
                  loading={isLoading}
                  disabled={isLoading || !data}
                  onChange={(checked) =>
                    handleAccountPreferenceChange(key, label, checked)
                  }
                  aria-label={`${label} email notifications`}
                />
              </SwitchCell>
            </PreferenceRow>
          ))}
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>My customers</SectionTitle>
            <ColumnLabel>Email</ColumnLabel>
          </SectionHeader>
          <PreferenceRow>
            <RowText>
              <RowLabel>Customers should receive email</RowLabel>
              <RowDescription>
                Notify customers for successful payment on virtual account and
                Klasha pay.
              </RowDescription>
            </RowText>
            <SwitchCell>
              <PreferenceSwitch
                checked={data?.customerPayments ?? false}
                loading={isLoading}
                disabled={isLoading || !data}
                onChange={handleCustomerPaymentsChange}
                aria-label="Customer payment emails"
              />
            </SwitchCell>
          </PreferenceRow>
        </Section>
      </ModalBody>
    </CustomModal>
  );
};

export default NotificationPreferencesModal;
